/**
 * MigrationProgressPill
 *
 * Floating bottom-right pill that stays visible while the migration is
 * active (or until the user dismisses it post-completion). Clicking it
 * reopens the modal. Mounted alongside the modal in the layout wrapper
 * so it's always in the DOM regardless of the active route panel.
 *
 * The pill is intentionally minimal — just a progress bar + percent +
 * tap target. The full breakdown lives in the modal.
 */

import React, { useEffect } from 'react';
import { Box, Flex, Text, Progress } from '@chakra-ui/react';
import { Loader2, CheckCircle2 } from 'lucide-react';

import { useMigrationProgress } from '@/providers/migration-progress-provider';

// Inject @keyframes once (same name the modal uses). Idempotent.
function useSpinKeyframes() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('__migration_spin_kf__')) return;
    const style = document.createElement('style');
    style.id = '__migration_spin_kf__';
    style.textContent =
      '@keyframes __mig_spin__{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }, []);
}

export const MigrationProgressPill: React.FC = () => {
  const {
    isMigrationActive,
    isScanActive,
    isModalOpen,
    openModal,
    typesProgressPercent,
    scanProgressPercent,
    migrationTypes,
    scanTypes,
    activePhase,
  } = useMigrationProgress();
  useSpinKeyframes();

  // Pick the stream that drives the pill, matching the modal's logic so
  // the two surfaces stay in sync.
  const phase: 'scan' | 'migration' =
    activePhase ??
    (migrationTypes.length > 0
      ? 'migration'
      : scanTypes.length > 0
        ? 'scan'
        : 'migration');
  const rows = phase === 'scan' ? scanTypes : migrationTypes;
  const progressPercent =
    phase === 'scan' ? scanProgressPercent : typesProgressPercent;
  const phaseActive = phase === 'scan' ? isScanActive : isMigrationActive;
  const label = phase === 'scan' ? 'Scanning' : 'Migration';

  // We render the pill while the phase is active OR once it's done until
  // the user closes it from the modal. After that, the pill is gone for
  // this session.
  const allFinished =
    rows.length > 0 &&
    rows.every((t) => t.status === 'complete' || t.status === 'failed');

  const shouldShow =
    !isModalOpen && (phaseActive || (allFinished && progressPercent < 100));

  if (!shouldShow) return null;

  return (
    <Box
      position="fixed"
      bottom={6}
      right={6}
      zIndex={1400}
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="full"
      boxShadow="lg"
      px={4}
      py={2}
      minW="260px"
      cursor="pointer"
      onClick={openModal}
      _hover={{ borderColor: 'blue.300', boxShadow: 'xl' }}
      transition="all 0.15s ease-in-out"
      role="button"
      aria-label={`Open ${label.toLowerCase()} progress`}
    >
      <Flex align="center" gap={3}>
        <Box
          style={
            phaseActive
              ? {
                  animation: '__mig_spin__ 1s linear infinite',
                  display: 'inline-flex',
                }
              : { display: 'inline-flex' }
          }
        >
          {allFinished ? (
            <CheckCircle2 size={18} color="#06AC38" />
          ) : (
            <Loader2 size={18} color="#0052cc" />
          )}
        </Box>
        <Box flex={1}>
          <Flex justify="space-between" mb={1}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
              {label}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {progressPercent}%
            </Text>
          </Flex>
          <Progress
            value={progressPercent}
            size="xs"
            colorScheme={allFinished ? 'green' : 'blue'}
            borderRadius="full"
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default MigrationProgressPill;
